import { useState, useEffect, useRef } from 'react';
import { InputTextCustom, Combobox, Modal, InputDate, InputPhone, initialStatePhoneNumber, mobilePhoneToString, validMobileForInputPhone } from 'medere-ui-framework';
import './CreatePatientView.scss';
import { Icon } from '@iconify/react';
import { HealthPatientCoverage, defaultHealthPatientCoverage, defaultHealthEntity, defaultHealthEntityPlan, defaultNewPatientRecordDTO, HealthEntityPlan, HealthEntity, PatientRecordDTO } from '../../../../interface/interface';
import { Button } from 'primereact/button';
import { ERecipeViews, ESteps, EAgendaViews } from '../../RecipeEnums';
import { isValidPatientRecord } from '../../patientRecord';

import { savePatient, getAllHealthEntities, getAllHealthEntityPlanByHealthEntityId, isGenderNull } from '../../../../services/digitalRPservice';
import { validateInternationalCellphone } from '../../../../utils/utils';
import { showMessage } from '../../../../utils/messages';
import { Toast, ToastMessage } from 'primereact/toast';
import CustomCheckBox from '../../../../components/CustomCheckBox/CustomCheckBox';

export default function CreatePatientView({ selectedPatient, setSelectedPatient, onBack, onBackFromEdit, setActiveView, subActiveView, setSubActiveView, prevActiveView, setPrevActiveView, prevSubActiveView, setPrevSubActiveView }: { selectedPatient: PatientRecordDTO | null, setSelectedPatient: (patient: PatientRecordDTO | null) => void, onBack: () => void, onBackFromEdit: () => void, setActiveView: (av: number | undefined) => void, subActiveView: number | undefined, setSubActiveView: (sav: number | undefined) => void, prevActiveView: number | undefined, setPrevActiveView: (pav: number | undefined) => void, prevSubActiveView: number | undefined, setPrevSubActiveView: (psav: number | undefined) => void }) {
    const [newPatient, setNewPatient] = useState<PatientRecordDTO>(selectedPatient ? selectedPatient : defaultNewPatientRecordDTO);
    const [birthdate, setBirthdate] = useState<Date | null>(selectedPatient && selectedPatient.birthdate ? generateDate(selectedPatient.birthdate.toString()) : null);
    const [insuranceModalVisible, setInsuranceModalVisible] = useState(false);
    const [insuranceToEdit, setInsuranceToEdit] = useState<HealthPatientCoverage | null>(null);
    const [selectedCoverage, setSelectedCoverage] = useState<HealthPatientCoverage | null>(defaultHealthPatientCoverage);
    const [selectedHealthEntity, setSelectedHealthEntity] = useState<HealthEntity>(defaultHealthEntity)
    const [selectedHealthEntityPlan, setSelectedHealthEntityPlan] = useState<HealthEntityPlan>(defaultHealthEntityPlan)
    const [validationInsuranceModal, setValidationInsuranceModal] = useState(false);
    const [notValidationPatientData, setNotValidationPatientData] = useState(false);
    const [mandatory, setMandatory] = useState(false)
    const [primaryCoverage, setPrimaryCoverage] = useState(true)
    const [saving, setSaving] = useState(false)
    const [openEditing, setOpenEditing] = useState(false)
    const [primaryCoverageErrorModal, setPrimaryCoverageErrorModal] = useState(false)
    const [patientCreatedSuccessfullyModal, setPatientCreatedSuccessfullyModal] = useState(false)

    const [insurances, setInsurances] = useState<any[]>(selectedPatient && selectedPatient?.insurances ? selectedPatient.insurances : []);
    const [insurancesToDelete, setInsurancesToDelete] = useState<number[]>([]);

    const [affiliateError, setAffiliateError] = useState(false);

    const [paddingTop, setPaddingTop] = useState('55%');
    const [isMobile, setisMobile] = useState(window.innerWidth < 700)
    const [documentModalError, setDocumentModalError] = useState(false)

    const [patientPhone, setPatientPhone] = useState(validMobileForInputPhone(selectedPatient?.mobilephone ?? ''));

    const toast = useRef(null)

    const showMessage = (ref: React.RefObject<Toast>, msg: any, severity: ToastMessage['severity']) => {
        ref.current?.show({ severity: severity, summary: msg, life: 3000 });
    };
    const showLongMessage = (ref: React.RefObject<Toast>, msg: any, severity: ToastMessage['severity']) => {
        const content = (
            <div style={{ fontWeight: 600 }}>
                {msg}
            </div>
        );
        ref.current?.show({ severity: severity, summary: msg, life: 3000, content: content });
    };


    const genderPatientNull = async () => {
        const genderNull: boolean | null = await isGenderNull(selectedPatient?.medereentity ?? -1)
            .then((res) => {
                return res
            })
            .catch((error) => {
                console.error('Error: ', error);
                return true;
            });
        setGender(genderNull ? null : gender)
        return genderNull
    };
    const [gender, setGender] = useState(selectedPatient ? (selectedPatient.gender === 'M' ? 'Masculino' : 'Femenino') : null)

    //Al editar un paciente con datos obligatorios vacíos (ej. el paso Paciente de la receta lo mandó a completarlos), se marca cada campo que falta
    const editing = !!selectedPatient;
    const missing = {
        firstname: editing && !newPatient?.firstname?.trim(),
        lastname: editing && !newPatient?.lastname?.trim(),
        document: editing && !newPatient?.document?.trim(),
        gender: editing && !gender,
        birthdate: editing && !birthdate,
    };

    useEffect(() => {
        genderPatientNull()

        const computePaddingTop = () => { //padding para las modales
            const minWidth = 430;
            const maxWidth = 1024;
            const minPadding = 20;
            const maxPadding = 50;
            const windowWidth = window.innerWidth;

            if (windowWidth <= minWidth) {
                setPaddingTop(`${maxPadding}%`);
            } else if (windowWidth >= maxWidth) {
                setPaddingTop(`${minPadding}%`);
            } else {
                const padding = maxPadding - ((windowWidth - minWidth) / (maxWidth - minWidth)) * (maxPadding - minPadding);
                setPaddingTop(`${padding}%`);
            }
        };

        computePaddingTop();
        window.addEventListener('top', computePaddingTop);

        const handleResize = () => {
            setisMobile(window.innerWidth < 700);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('top', computePaddingTop);
        };
    }, []);

    useEffect(() => {
        validateInsuranceModal();
        if (saving && selectedCoverage?.healthentityplan) {
            if (insuranceToEdit) {
                const updatedInsurances = insurances.map((insurance: HealthPatientCoverage) =>
                    insurance === insuranceToEdit ? selectedCoverage : insurance
                );
                setInsurances(updatedInsurances);
            } else {
                setInsurances([...insurances, selectedCoverage]);
            }
            setInsuranceModalVisible(false);
            setInsuranceToEdit(undefined);
            setSaving(false)
        }
    }, [selectedCoverage])

    useEffect(() => {
        if (!insuranceModalVisible) {
            setInsuranceToEdit(undefined)
            setSelectedCoverage(defaultHealthPatientCoverage)
            setSelectedHealthEntity(defaultHealthEntity)
            setSelectedHealthEntityPlan(defaultHealthEntityPlan)
            setMandatory(false)
            setPrimaryCoverage(true)
            setAffiliateError(false)
        }
    }, [insuranceModalVisible])

    useEffect(() => {
        if (!openEditing)
            setSelectedHealthEntityPlan(defaultHealthEntityPlan)
        else
            setOpenEditing(false)

        if (!selectedHealthEntity)
            setSelectedHealthEntity(defaultHealthEntity)
    }, [selectedHealthEntity])

    const handleInsuranceModalSave = () => {
        if (primaryCoverage) { //si es true, setea en false el primarycoverage de todas las insurances. Si se estaba editando, lo hace para todas menos al que edita
            const updatedInsurances = insurances.map(insurance => {
                if (insuranceToEdit && insurance.healthentityplan.healthentity.healthentity === insuranceToEdit.healthentityplan.healthentity.healthentity)
                    return insurance;
                else
                    return { ...insurance, primarycoverage: false };
            });
            setInsurances(updatedInsurances);
        }

        const healthentityplan: HealthEntityPlan = { healthentityplan: selectedHealthEntityPlan.healthentityplan, name: selectedHealthEntityPlan.name, healthentity: selectedHealthEntity }
        setSelectedCoverage({ ...selectedCoverage, healthentityplan: healthentityplan, voluntary: !mandatory, primarycoverage: primaryCoverage, healthpatientcoverage: selectedCoverage?.healthpatientcoverage ?? 0, benefitnumber: selectedCoverage?.benefitnumber ?? '', coveragestart: selectedCoverage?.coveragestart ?? new Date(), coverageend: selectedCoverage?.coverageend ?? new Date(), patient: selectedCoverage?.patient ?? 0, description: selectedCoverage?.description ?? '' })
        //va al useEffect de selectedCoverage
    };

    const handleInsuranceModalCancel = () => {
        setInsuranceModalVisible(false);
        setInsuranceToEdit(undefined);
        setSelectedCoverage(defaultHealthPatientCoverage);
    };

    const handleEditInsurance = (insurance: HealthPatientCoverage) => {
        setSelectedHealthEntity({ healthentity: insurance.healthentityplan.healthentity.healthentity, commercialname: insurance.healthentityplan.healthentity.commercialname, recipevalidatorid: insurance.healthentityplan.healthentity.recipevalidatorid })
        setSelectedHealthEntityPlan({ healthentityplan: insurance.healthentityplan.healthentityplan, name: insurance.healthentityplan.name, healthentity: selectedHealthEntity })
        setInsuranceToEdit(insurance);
        setPrimaryCoverage(insurance.primarycoverage)
        setSelectedCoverage(insurance);
        setMandatory(!insurance.voluntary)
        setOpenEditing(true)
        setInsuranceModalVisible(true);
    };

    const handleDeleteInsurance = (insurance: HealthPatientCoverage) => {
        if (insurance.healthpatientcoverage)
            setInsurancesToDelete([...insurancesToDelete, insurance.healthpatientcoverage])
        setInsurances(insurances.filter((i) => i !== insurance));
    };

    const handleAddInsurance = () => {
        setInsuranceModalVisible(true);
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setNewPatient({ ...newPatient, [name]: value });
    };

    const validateInsuranceModal = () => {
        if (!selectedCoverage) {
            setValidationInsuranceModal(false);
            return;
        }
        if (!selectedHealthEntity?.commercialname || !selectedHealthEntityPlan?.name || !selectedCoverage?.benefitnumber || !testBenefitNumber()) {
            setValidationInsuranceModal(false);
            return;
        }
        setValidationInsuranceModal(true);
    };

    const handleSaveOrEdit = () => {
        //const cleanedNumber =  .number.replace(/\D/g, '');
        //const fullPhone = `${patientPhone.prefix} ${cleanedNumber}`;
        if (newPatient.mobilephone && !validateInternationalCellphone(newPatient.mobilephone).isValid()) {
            showMessage(toast, validateInternationalCellphone(newPatient.mobilephone).errorMessage!, 'error');
            return;
        }

        if (insurances.length === 1)
            insurances[0].primarycoverage = true

        if (insurances.length >= 1 && insurances.every(insurance => !insurance.primarycoverage)) {
            setPrimaryCoverageErrorModal(true)
        }
        else if (newPatient.document.length < 6)
            setDocumentModalError(true)
        else {
            setSaving(true)
            setNewPatient({ ...newPatient, insurances: insurances, insurancesToDelete: insurancesToDelete }) //va al useEffect de newPatient
        }
    }

    const handleCancel = () => {
        if (prevActiveView === ERecipeViews.NEWRECIPE && prevSubActiveView === ESteps.PATIENTSTEP && selectedPatient && !isValidPatientRecord(selectedPatient)) {
            //El paso Paciente lo mandó a completar datos que faltan: volver a ese paso lo mandaría acá de nuevo, así que vuelve a la pantalla inicial de recetas y órdenes
            setSelectedPatient(null);
            setSubActiveView(0);
            setPrevSubActiveView(undefined);
            setActiveView(0);
            setPrevActiveView(undefined);
        } else if ((prevActiveView && prevSubActiveView) || (prevActiveView === 0 && prevSubActiveView === 0)) {
            if (prevActiveView === ERecipeViews.NEWRECIPE && (prevSubActiveView === ESteps.CONFIRMATIONSTEP/*vuelve de credenciales invalidas en ConfirmationStep*/ || prevSubActiveView === ESteps.PATIENTSTEP/*vuelve de editar paciente en PatientStep*/)) {
                setSubActiveView(prevSubActiveView);
                setPrevSubActiveView(EAgendaViews.CREATEPATIENTVIEW);
                setActiveView(prevActiveView);
                setPrevActiveView(ERecipeViews.PATIENTAGENDA);
            } else {
                setSubActiveView(prevSubActiveView);
                setPrevSubActiveView(undefined);
                setActiveView(prevActiveView);
                setPrevActiveView(undefined);
            }
        } else if (selectedPatient)
            onBackFromEdit();
        else
            onBack();
    }

    useEffect(() => {
        if (!saving)
            validatePatientData();
        else {
            savePatient(newPatient, 0, 0)
                .then((res) => {
                    setSelectedPatient(res) //va al useEffect
                })
                .catch(error => {
                    console.error('Error:', error)
                });
        }
    }, [newPatient])

    useEffect(() => {
        if (saving) {
            setInsurancesToDelete([])
            setSaving(false)
            setPatientCreatedSuccessfullyModal(true)
        }
    }, [selectedPatient])

    useEffect(() => {
        setNewPatient({ ...newPatient, insurances: insurances })
    }, [insurances])

    const validatePatientData = () => {
        setNotValidationPatientData(!newPatient?.firstname || !newPatient?.lastname || !newPatient?.document || !newPatient?.gender || !newPatient?.birthdate);
    };

    const testBenefitNumber = () => {
        return /^[A-Za-z0-9]*$/.test(selectedCoverage?.benefitnumber ?? '');
    }

    useEffect(() => {
        if (selectedCoverage?.benefitnumber) {
            setAffiliateError(!testBenefitNumber())
        }
    }, [selectedCoverage?.benefitnumber])

    const genderOptions = [
        { id: 'M', name: 'Masculino' },
        { id: 'F', name: 'Femenino' }
    ];

    function generateDate(date: string) {
        const [year, month, auxDay] = date.split('-'); //fecha viene como yyyy-MM-dd
        const day = auxDay.split('T')[0] //si editas fecha y guardas, y volves a editar, viene como yyyy-MM-ddYxxxxxx

        const formattedMonth = month.length < 2 ? `0${month}` : month;
        const formattedDay = day.length < 2 ? `0${day}` : day;
        const formattedDate = `${year}-${formattedMonth}-${formattedDay}T03:00:00.000Z`;

        const finalDate = new Date(formattedDate) //aplicar new Date hace que a veces el día pueda ser menor o mayor dependiendo del cambio de horario. Los ifs están para controlar que vuelva al día original en caso de cambiarse

        const comparableDay = day < '10' ? day.slice(1, 2) : day //si el día es menor a 10, y solo me funciona string para comparar, tengo que quitarle el '0' a, por ej, '02' de 'day' para comparar con '2' de 'finalDate.getDate().toString()'

        if (finalDate.getDate().toString() < comparableDay && (finalDate.getMonth() + 1).toString() <= month && finalDate.getFullYear().toString() === year) { //si el día formateado es menor al original, dentro del mismo mes y año, o mes anterior, le suma un día
            finalDate.setDate(finalDate.getDate() + 1)
        }
        if (finalDate.getDate().toString() > comparableDay && (finalDate.getMonth() + 1).toString() >= month && finalDate.getFullYear().toString() === year) { //si el día formateado es mayor al original, dentro del mismo mes y año, o mes siguiente, le resta un día
            finalDate.setDate(finalDate.getDate() - 1)
        }
        if (finalDate.getFullYear().toString() < year) { //si cambio de año a uno anterior, le sumo el día faltante
            finalDate.setDate(finalDate.getDate() + 1)
        }
        if (finalDate.getFullYear().toString() > year) { //si cambio de año a uno siguiente, le quito el dia de más
            finalDate.setDate(finalDate.getDate() - 1)
        }

        const updatedDate = finalDate

        return updatedDate;
    }

    return (
        <div className="create-patient-view" style={{ padding: '15px' }}>
            {isMobile ?
                <div>
                    <div style={{ display: 'flex', flexDirection: 'row', marginTop: '8px', fontSize: '22px' }}>
                        <h1 style={{ fontWeight: 600 }}>{selectedPatient ? "Editar" : "Crear"}</h1>
                        <h1 style={{ marginLeft: '5px' }}>paciente</h1>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Nombre</label>
                        <InputTextCustom
                            className={missing.firstname ? '' : 'border2'}
                            error={missing.firstname}
                            caption={missing.firstname ? 'Hace falta completar el nombre' : ''}
                            name="firstname"
                            value={newPatient?.firstname}
                            onChange={handleInputChange}
                        />
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Apellido</label>
                        <InputTextCustom
                            className={missing.lastname ? '' : 'border2'}
                            error={missing.lastname}
                            caption={missing.lastname ? 'Hace falta completar el apellido' : ''}
                            name="lastname"
                            value={newPatient.lastname}
                            onChange={handleInputChange}
                        />
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>DNI</label>
                        <InputTextCustom
                            className={missing.document ? '' : 'border2'}
                            error={missing.document}
                            caption={missing.document ? 'Hace falta completar el DNI' : ''}
                            name="document"
                            value={newPatient.document}
                            onChange={handleInputChange}
                        />
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Sexo</label>
                        <Combobox
                            className={missing.gender ? '' : 'border2'}
                            error={missing.gender}
                            caption={missing.gender ? 'Hace falta completar el sexo' : ''}
                            getItems={() => Promise.resolve(genderOptions)}
                            optionLabel="name"
                            value={genderOptions.find((option) => option.name === gender)}
                            setValue={(e) => {
                                setGender(e.name)
                                setNewPatient({ ...newPatient, gender: e.id })
                            }}
                        />
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Fecha de Nacimiento</label>
                        <InputDate
                            className={missing.birthdate ? '' : 'border2'}
                            error={missing.birthdate}
                            caption={missing.birthdate ? 'Hace falta completar la fecha de nacimiento' : ''}
                            name='birthdate'
                            value={birthdate}
                            onChange={(e) => {
                                setNewPatient({ ...newPatient, birthdate: e.target.value })
                                setBirthdate(e.target.value)
                            }}
                            maxDate={new Date()}
                        />
                        <InputPhone
                            cboClassname='border2 w-4'
                            inputClassname='border2'
                            value={patientPhone}
                            label="Celular. Característica sin 0 y número sin 15. (Ejemplo: 2231234567)"
                            floatLabel={false}
                            setValue={(mobile) => {
                                setPatientPhone(mobile);
                                setNewPatient({ ...newPatient, mobilephone: mobilePhoneToString(mobile) });
                            }}
                        />
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Mail</label>
                        <InputTextCustom
                            className="border2"
                            name="email"
                            value={newPatient.email}
                            onChange={handleInputChange}
                        />


                        <div style={{ boxShadow: '0 4px 4px rgba(0,0,0,0.15)' }} className="collapsible-card">
                            <div className='content-header'>
                                <div className="header">
                                    <div style={{ color: '#B27668' }} className="title">COBERTURA MEDICA</div>
                                </div>
                            </div>
                            <div className="content">
                                <Button
                                    className='w-full'
                                    label="Agregar"
                                    onClick={handleAddInsurance}
                                    style={{ backgroundColor: '#FFFFFF', color: '#545454', height: '36px', borderColor: '#3CB8BA' }}
                                />
                                {insurances.map((insurance: HealthPatientCoverage, index) => (
                                    <div key={index} className="insurance-item" style={{ borderBottom: index !== insurances.length - 1 ? '0.1px solid #A9B1BC' : '', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                        <span style={{ marginTop: '9px' }}>{insurance?.healthentityplan?.healthentity?.commercialname}</span>
                                        <div>
                                            <Button style={{ background: 'none', border: 'none', padding: '0px' }} onClick={() => handleEditInsurance(insurance)}>
                                                <Icon icon='bi:pencil-square' style={{ color: '#3CB8BA', fontSize: '17px', marginBottom: '10px' }} />
                                            </Button>
                                            <Button style={{ color: '#3CB8BA', background: 'none', border: 'none', padding: '0px', paddingTop: '7px', marginLeft: '5px', paddingBottom: '7px' }} onClick={() => handleDeleteInsurance(insurance)}>
                                                <Icon icon='bi:x' style={{ color: '#3CB8BA', fontSize: '25px' }} />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ boxShadow: '0 4px 4px rgba(0,0,0,0.15)' }} className="collapsible-card">
                            <div className='content-header'>
                                <div className="header">
                                    <div style={{ color: '#B27668' }} className="title">OBSERVACIONES</div>
                                </div>
                            </div>
                            <div className="content">
                                <InputTextCustom
                                    className="border2"
                                    name="observations"
                                    value={newPatient.observations}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div style={{ paddingTop: '5px', display: 'flex', flexDirection: 'column' }}>
                            <Button
                                label={selectedPatient ? "Guardar" : "Crear"}
                                onClick={handleSaveOrEdit}
                                disabled={notValidationPatientData}
                                style={{ marginBottom: '15px', backgroundColor: !notValidationPatientData ? '#14C7BC' : '#9E9E9E', border: 'none', height: '36px' }}
                            />
                            <Button
                                label="Cancelar"
                                onClick={handleCancel}
                                style={{ backgroundColor: '#FFFFFF', color: '#545454', height: '36px', borderColor: '#3CB8BA' }}
                            />
                        </div>
                    </div>

                    <Modal visible={primaryCoverageErrorModal} setVisible={setPrimaryCoverageErrorModal}
                        header={<div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', paddingTop: paddingTop, paddingLeft: '10%' }}>
                            <i className="pi pi-exclamation-circle" style={{ fontSize: '48px', color: '#B67345' }}></i>
                        </div>}
                        headerStyle={{ fontSize: '12px', textAlign: 'center' }}
                        className='h-full'
                    >
                        Debe haber al menos una Obra social marcada como favorita
                    </Modal>
                    <Modal visible={patientCreatedSuccessfullyModal} setVisible={setPatientCreatedSuccessfullyModal}
                        header={<div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', paddingTop: paddingTop, paddingLeft: '10%' }}>
                            <i className="pi pi-check-circle" style={{ fontSize: '48px', color: '#B67345' }}></i>
                        </div>}
                        headerStyle={{ fontSize: '12px', textAlign: 'center', paddingTop: paddingTop }}
                        hideAutomatically onHideCustom={handleCancel}
                        className='h-full'
                    >
                        {selectedPatient ? "Cambios del paciente guardados exitosamente" : "Paciente creado exitosamente"}
                    </Modal>
                    <Modal visible={documentModalError} setVisible={setDocumentModalError}
                        header={<div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', paddingTop: paddingTop, paddingLeft: '10%' }}>
                            <i className="pi pi-exclamation-circle" style={{ fontSize: '48px', color: '#B67345' }}></i>
                        </div>}
                        headerStyle={{ fontSize: '12px', textAlign: 'center', paddingTop: paddingTop }}
                        className='h-full'
                    >
                        El documento debe tener al menos 6 dígitos
                    </Modal>
                </div>
                :
                <div>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', flexDirection: 'row', marginTop: '8px', fontSize: '20px' }}>
                            <h1 style={{ fontWeight: 600 }}>{selectedPatient ? "Editar" : "Crear"}</h1>
                            <h1 style={{ marginLeft: '5px' }}>paciente</h1>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '0 1rem' }}>
                        <div style={{ flex: '1', marginRight: '20px' }}>
                            <div style={{ display: 'flex', gap: '20px' }}>
                                <div style={{ flex: 1 }}>
                                    <label>Nombre</label>
                                    <InputTextCustom
                                        className={missing.firstname ? '' : 'border2'}
                                        error={missing.firstname}
                                        caption={missing.firstname ? 'Hace falta completar el nombre' : ''}
                                        name="firstname"
                                        value={newPatient?.firstname}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label>Apellido</label>
                                    <InputTextCustom
                                        className={missing.lastname ? '' : 'border2'}
                                        error={missing.lastname}
                                        caption={missing.lastname ? 'Hace falta completar el apellido' : ''}
                                        name="lastname"
                                        value={newPatient?.lastname}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label>DNI</label>
                                    <InputTextCustom
                                        className={missing.document ? '' : 'border2'}
                                        error={missing.document}
                                        caption={missing.document ? 'Hace falta completar el DNI' : ''}
                                        name="document"
                                        value={newPatient?.document}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label>Sexo</label>
                                    <Combobox
                                        className={missing.gender ? '' : 'border2'}
                                        error={missing.gender}
                                        caption={missing.gender ? 'Hace falta completar el sexo' : ''}
                                        getItems={() => Promise.resolve(genderOptions)}
                                        optionLabel="name"
                                        value={genderOptions.find((option) => option.name === gender)}
                                        setValue={(e) => {
                                            setGender(e.name)
                                            setNewPatient({ ...newPatient, gender: e.id })
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label>Fecha de Nacimiento</label>
                                    <InputDate
                                        className={missing.birthdate ? '' : 'border2'}
                                        error={missing.birthdate}
                                        caption={missing.birthdate ? 'Hace falta completar la fecha de nacimiento' : ''}
                                        name='birthdate'
                                        value={birthdate}
                                        onChange={(e) => {
                                            setNewPatient({ ...newPatient, birthdate: e.target.value });
                                            setBirthdate(e.target.value);
                                        }}
                                        maxDate={new Date()}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <InputPhone
                                        cboClassname='border2 w-4'
                                        inputClassname='border2'
                                        value={patientPhone}
                                        label="Celular. Característica sin 0 y número sin 15. (Ejemplo: 2231234567)"
                                        floatLabel={false}
                                        setValue={(mobile) => {
                                            setPatientPhone(mobile);
                                            setNewPatient({ ...newPatient, mobilephone: mobilePhoneToString(mobile) });
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label>Mail</label>
                                    <InputTextCustom
                                        className="border2"
                                        name="email"
                                        value={newPatient?.email}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={{ boxShadow: '0 4px 4px rgba(0,0,0,0.15)', marginBottom: '20px', display: 'flex', flexDirection: 'column'/*, justifyContent: 'space-between', alignItems: 'center'*/, padding: '10px' }} className="collapsible-card">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div className="content-header" style={{ flex: 1 }}>
                                    <div className="header">
                                        <div style={{ color: '#B27668' }} className="title">COBERTURA MEDICA</div>
                                    </div>
                                </div>
                                <div className="content" style={{ flex: 1 }}>
                                    <Button
                                        className='w-auto btnRpBack'
                                        label="Agregar"
                                        onClick={handleAddInsurance}
                                        style={{
                                            backgroundColor: '#FFFFFF',
                                            color: '#545454',
                                            height: '36px',
                                            borderColor: '#3CB8BA',
                                            minWidth: '100%',
                                            marginLeft: 'auto'
                                        }}
                                    />
                                </div>
                            </div>
                            <div>
                                {insurances.map((insurance: HealthPatientCoverage, index) => (
                                    <div key={index} className="insurance-item" style={{ borderBottom: index !== insurances.length - 1 ? '0.1px solid #A9B1BC' : '', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                        <span style={{ marginTop: '9px' }}>{insurance?.healthentityplan?.healthentity?.commercialname}</span>
                                        <div>
                                            <Button style={{ background: 'none', border: 'none', padding: '0px' }} onClick={() => handleEditInsurance(insurance)}>
                                                <Icon icon='bi:pencil-square' style={{ color: '#3CB8BA', fontSize: '17px', marginBottom: '10px' }} />
                                            </Button>
                                            <Button style={{ color: '#3CB8BA', background: 'none', border: 'none', padding: '0px', paddingTop: '7px', marginLeft: '5px', paddingBottom: '7px' }} onClick={() => handleDeleteInsurance(insurance)}>
                                                <Icon icon='bi:x' style={{ color: '#3CB8BA', fontSize: '25px' }} />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ boxShadow: '0 4px 4px rgba(0,0,0,0.15)' }} className="collapsible-card">
                            <div className="content-header">
                                <div className="header">
                                    <div style={{ color: '#B27668' }} className="title">OBSERVACIONES</div>
                                </div>
                            </div>
                            <div className="content">
                                <InputTextCustom
                                    className="border2"
                                    name="observations"
                                    value={newPatient?.observations}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'left', marginTop: '20px', paddingLeft: '1rem' }}>
                        <Button
                            className='btnRpContinue'
                            label={selectedPatient ? "Guardar" : "Crear"}
                            onClick={handleSaveOrEdit}
                            disabled={notValidationPatientData}
                            style={{ marginRight: '15px', backgroundColor: !notValidationPatientData ? '#14C7BC' : '#9E9E9E', border: 'none', height: '36px', width: '177px' }}
                        />
                        <Button
                            className='btnRpBack'
                            label="Cancelar"
                            onClick={handleCancel}
                            style={{ backgroundColor: '#FFFFFF', color: '#545454', height: '36px', borderColor: '#3CB8BA', width: '177px' }}
                        />
                    </div>

                    <Modal visible={primaryCoverageErrorModal} setVisible={setPrimaryCoverageErrorModal}
                        header={<i className="pi pi-exclamation-circle" style={{ fontSize: '48px', color: '#B67345' }}></i>}
                        headerStyle={{ fontSize: '12px', textAlign: 'center' }}
                        width='w-medium'
                    >
                        Debe haber al menos una Obra social marcada como favorita
                    </Modal>
                    <Modal visible={patientCreatedSuccessfullyModal} setVisible={setPatientCreatedSuccessfullyModal}
                        header={<i className="pi pi-check-circle" style={{ fontSize: '48px', color: '#B67345' }}></i>}
                        headerStyle={{ fontSize: '12px', textAlign: 'center' }}
                        hideAutomatically onHideCustom={handleCancel}
                        width='w-medium'
                    >
                        {selectedPatient ? "Cambios del paciente guardados exitosamente" : "Paciente creado exitosamente"}
                    </Modal>
                    <Modal visible={documentModalError} setVisible={setDocumentModalError}
                        header={<i className="pi pi-exclamation-circle" style={{ fontSize: '48px', color: '#B67345' }}></i>}
                        headerStyle={{ fontSize: '12px', textAlign: 'center' }}
                        width='w-medium'
                    >
                        El documento debe tener al menos 6 dígitos
                    </Modal>
                </div>
            }

            <Modal
                visible={insuranceModalVisible}
                setVisible={setInsuranceModalVisible}
                header={<label><strong>{insuranceToEdit ? 'Editar' : ''} Cobertura</strong> médica</label>}
                headerStyle={{ fontSize: '12px' }}
                className="h-full"
            >
                <div className="create-patient-view">
                    <div style={{ height: 'calc(100vh - 295px)', overflowX: 'auto', overflowY: 'auto', paddingTop: '10px', borderRadius: '4px' }}>
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Obra Social</label>
                        <Combobox
                            className="border2 mb-4"
                            getItems={getAllHealthEntities}
                            optionLabel="commercialname"
                            value={selectedHealthEntity}
                            setValue={setSelectedHealthEntity}
                        />
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>Plan</label>
                        <Combobox
                            className="border2 mb-4"
                            getItems={getAllHealthEntityPlanByHealthEntityId}
                            params={{ healthEntityId: selectedHealthEntity?.healthentity }}
                            optionLabel="name"
                            value={selectedHealthEntityPlan}
                            setValue={setSelectedHealthEntityPlan}
                            disable={selectedHealthEntity?.healthentity <= 0}
                        />
                        <label style={{ color: '#1E1E1E', fontSize: '14px' }}>N° de afiliado</label>
                        <InputTextCustom
                            className={affiliateError ? "" : "border2"}
                            error={affiliateError}
                            caption={affiliateError ? 'El n° de afiliado solo puede contener letras y/o números' : ''}
                            value={selectedCoverage ? selectedCoverage.benefitnumber : ''}
                            onChange={(e) => {
                                setSelectedCoverage({ ...selectedCoverage, benefitnumber: e.target.value, healthpatientcoverage: selectedCoverage?.healthpatientcoverage ?? 0, coveragestart: selectedCoverage?.coveragestart ?? new Date(), coverageend: selectedCoverage?.coverageend ?? new Date(), primarycoverage: selectedCoverage?.primarycoverage ?? false, voluntary: selectedCoverage?.voluntary ?? false, healthentityplan: selectedCoverage?.healthentityplan ?? defaultHealthEntityPlan, patient: selectedCoverage?.patient ?? 0, description: selectedCoverage?.description ?? '' })
                            }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ marginBottom: '0.5rem' }}>
                                <CustomCheckBox
                                    checked={primaryCoverage}
                                    onChange={(e) => setPrimaryCoverage(e.target.checked ?? false)}
                                />
                                <span style={{ fontSize: '15px' }}>Usar esta Obra social como favorita.</span>
                            </label>
                            <label>
                                <CustomCheckBox
                                    checked={mandatory}
                                    onChange={(e) => setMandatory(e.target.checked ?? false)}
                                />
                                <span style={{ fontSize: '15px' }}>La cobertura es a través de su trabajo</span>
                            </label>
                        </div>
                    </div>
                    <div style={{ paddingTop: '5px', display: 'flex', flexDirection: 'column' }}>
                        <Button
                            className='btnRpContinue'
                            label={insuranceToEdit ? 'Guardar cambios' : 'Agregar cobertura'}
                            onClick={() => {
                                setSaving(true)
                                handleInsuranceModalSave()
                            }}
                            disabled={!validationInsuranceModal}
                            style={{ marginBottom: '15px', backgroundColor: validationInsuranceModal ? '#14C7BC' : '#9E9E9E', border: 'none', height: '36px' }}
                        />
                        <Button
                            className='btnRpBack'
                            label="Cancelar"
                            onClick={handleInsuranceModalCancel}
                            style={{ backgroundColor: '#FFFFFF', color: '#545454', height: '36px', borderColor: '#3CB8BA' }}
                        />
                    </div>
                </div>
            </Modal>
            <Toast ref={toast} position='top-right' /> {/*Toast tiene una prop onRemove, en el que hace algo en el momento que el toast se cierra (tanto despues de pasar un tiempo como por cerrarlo manualmente)*/}
        </div>
    );
}
